import { cn } from "@newxsystems/shared";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IProjectShowcaseBlockProps } from "./ProjectShowcaseBlock.props";

export const ProjectShowcaseBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IProjectShowcaseBlockProps) => {
  if (!items?.length) return null;
  const total = String(items.length).padStart(2, "0");

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        <div className="flex flex-col border-x border-b border-border">
          {items.map((entry, index) => (
            <DaylightCardLink
              className="group relative isolate min-h-[clamp(24rem,80vh,54rem)] overflow-hidden border-t border-border"
              key={index}
              link={entry.link}
            >
              <DaylightMedia
                className="absolute inset-0 h-full border-0"
                imageClassName="object-cover transition-transform duration-700 ease-out motion-reduce:transition-none group-hover:scale-105"
                media={entry.image.image}
                sizes="(min-width: 1600px) 1520px, 100vw"
              />
              <div className="absolute inset-0 bg-linear-to-t from-terminal via-terminal/30 to-terminal/40" />
              <div className="relative z-10 flex h-full min-h-[clamp(24rem,80vh,54rem)] flex-col justify-between gap-12 p-6 md:p-10">
                <div className="flex items-start justify-between gap-4">
                  <span className="daylight-label text-northsea">{entry.location}</span>
                  <span className="daylight-label text-primary">
                    {String(index + 1).padStart(2, "0")} / {total}
                  </span>
                </div>
                <div className="max-w-3xl">
                  {entry.category ? (
                    <p className="daylight-label mb-4 text-primary">{entry.category}</p>
                  ) : null}
                  <h3 className="mb-6 text-balance font-daylight-display text-[clamp(2rem,3.3vw,4rem)] font-bold leading-[0.9] tracking-[-0.04em] text-graphite">
                    {entry.title}
                  </h3>
                  <div className="grid max-w-2xl grid-cols-1 gap-6 md:grid-cols-3">
                    {entry.metricValue ? (
                      <div className="border-l border-primary pl-4">
                        {entry.metricLabel ? (
                          <p className="daylight-label-micro mb-1 text-northsea">
                            {entry.metricLabel}
                          </p>
                        ) : null}
                        <p className="font-daylight-display text-xl font-semibold text-graphite">
                          {entry.metricValue}
                        </p>
                      </div>
                    ) : null}
                    {entry.description ? (
                      <div
                        className={cn(
                          "border-l border-border pl-4",
                          entry.metricValue && "md:col-span-2",
                        )}
                      >
                        {entry.challengeLabel ? (
                          <p className="daylight-label-micro mb-1 text-northsea">
                            {entry.challengeLabel}
                          </p>
                        ) : null}
                        <DaylightRichText
                          className="text-sm leading-relaxed text-graphite"
                          content={entry.description}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

ProjectShowcaseBlock.displayName = "Block::ProjectShowcaseBlock";
